import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  ListItemText,
  Checkbox,
  OutlinedInput,
  Box,
  Chip,
  ListItemIcon,
} from "@mui/material";
import SelectArrow from "../styles/icons/SelectArrow";

/**
 * Extended multi‑select component that includes:
 *  • "Select All" option with tri‑state checkbox.
 */
export default function MultiSelectWithSelectAll({
  label,
  options = [],
  value,
  onChange,
}) {
  const [search, setSearch] = React.useState("");

  // Are all items currently selected?
  const allSelected = options.length > 0 && value.length === options.length;

  const filteredOptions = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((opt) =>
      String(opt?.label ?? "")
        .toLowerCase()
        .includes(query)
    );
  }, [options, search]);

  // Handle option selection/deselection
  const handleSelectChange = (e) => {
    let newValue = e.target.value; // array of item values user selected

    // If user clicked our special "Select All" item (with value="__all"):
    if (newValue.includes("__all")) {
      // If all are already selected, clear them; otherwise select them all
      newValue = allSelected ? [] : options.map((o) => o.value);
    }

    // Ensure it's always an array of strings/numbers
    if (typeof newValue === "string") {
      newValue = newValue.split(",");
    }

    onChange(newValue);
  };

  return (
    <FormControl
      variant="outlined"
      size="medium"
      sx={{
        width: "100%",
        maxWidth: "100%",
      }}
    >
      {label && (
        <InputLabel
          id={`${label}-label`}
          sx={{
            color: "#0DA3E2",
            "&.Mui-focused": { color: "#0DA3E2" },
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        labelId={label ? `${label}-label` : undefined}
        multiple
        value={value}
        IconComponent={SelectArrow}
        sx={{
          borderRadius: "0px",
          width: "100%",
          height: 64,
          minHeight: 64,
          maxHeight: 64,
          color: "#FFFFFF",
          bgcolor: "#1E1E1E",
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: "8px",
            paddingTop: "10px",
            paddingBottom: "10px",
            paddingLeft: "14px",
            paddingRight: "38px",
            minHeight: "22px",
            maxHeight: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "3px",
            borderColor: "#0DA3E2",
            borderWidth: "1px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0DA3E2",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0DA3E2",
          },
        }}
        onChange={handleSelectChange}
        input={<OutlinedInput label={label} />}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              bgcolor: "#1E1E1E",
              color: "#FFFFFF",
              border: "1px solid #0DA3E2",
              borderRadius: "3px",
              maxHeight: "220px",
              overflowY: "auto",
              "& .MuiMenuItem-root": {
                color: "#FFFFFF",
                minHeight: 44,
                fontSize: "14px",
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: "rgba(13,163,226,0.22)",
                },
                "&:hover": {
                  backgroundColor: "rgba(13,163,226,0.12)",
                },
              },
            },
          },
        }}
        renderValue={(selectedArray) => {
          if (selectedArray.length === 0) {
            return <span style={{ color: "#999" }}>None selected</span>;
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 0.5,
                width: "100%",
                maxHeight: "100%",
                overflowX: "auto",
                overflowY: "hidden",
              }}
            >
              {selectedArray.map((val) => {
                // Handle both object and primitive formats
                let actualValue, chipLabel;

                if (typeof val === "object" && val !== null) {
                  actualValue = val.value;
                  chipLabel = val.label;
                } else {
                  actualValue = val;
                  const matched = options.find((opt) => opt.value === val);
                  chipLabel = matched ? matched.label : val;
                }

                return (
                  <Chip
                    key={actualValue}
                    label={chipLabel}
                    size="small"
                    sx={{
                      height: "34px",
                      borderRadius: "40px",
                      fontSize: "20px",
                      fontWeight: 400,
                      color: "#FFFFFF",
                      bgcolor: "#0DA3E2",
                      maxWidth: 280,
                      "& .MuiChip-label": {
                        px: "14px",
                        lineHeight: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                );
              })}
            </Box>
          );
        }}
      >
        <ListSubheader
          sx={{
            bgcolor: "#1E1E1E",
            borderBottom: "1px solid rgba(13,163,226,0.25)",
            py: 1,
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <OutlinedInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            sx={{
              width: "100%",
              height: 38,
              color: "#FFFFFF",
              bgcolor: "#1E1E1E",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFFFFF",
                borderRadius: "3px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFFFFF",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFFFFF",
              },
            }}
          />
        </ListSubheader>

        {/* "Select All" row */}
        <MenuItem value="__all">
          <ListItemIcon>
            <Checkbox
              checked={allSelected}
              indeterminate={value.length > 0 && value.length < options.length}
            />
          </ListItemIcon>
          <ListItemText primary="Select All" />
        </MenuItem>

        {/* Normal items */}
        {filteredOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
        {filteredOptions.length === 0 && (
          <MenuItem disabled>No results</MenuItem>
        )}
      </Select>
    </FormControl>
  );
}
